import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject, firstValueFrom } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ApiClientService } from "../api-client.service";
import { ICommandManager, IMetaCommand } from "./commandManager";
import { EventService, IEventTransferObject } from "./event.service";
import { RuleValidationMessage } from "./models/rule-validation-message";

const TRANSACTION_ENDPOINT = "cqs/transactioncommands";
const PREVALIDATION_ENDPOINT = "cqs/prevalidate";

export interface ICommand {
    $type: string;
}
@Injectable({
    providedIn: "root"
})
export class CommandService {

    transactionCommandQueue: ICommand[] = [];
    commandsPushed = new Subject<ICommand[]>();
    commandsAdded = new Subject<ICommand>();

    constructor(private readonly apiClient: ApiClientService, private readonly eventService: EventService) { }

    addTransactionCommand<T extends ICommand>(command: T) {
        this.transactionCommandQueue.push(command);
        this.commandsAdded.next(command);
    }

    removeCommand(command: ICommand) {
        const index = this.transactionCommandQueue.indexOf(command);
        if (index > -1) {
            this.transactionCommandQueue.splice(index, 1);
        }
    }

    prevalidateCommands(commandManager: ICommandManager): Promise<RuleValidationMessage[]> {
        const stack = commandManager.getStack();
        return firstValueFrom(this.apiClient
            .post<ICommand[], any[]>(PREVALIDATION_ENDPOINT, stack));
    }

    pushCommands(commandManager: ICommandManager): Promise<IEventTransferObject> {
        const commandsToPush = commandManager.getStack();
        const metadataHeader = this.getMetaCommandsAsHeader(commandManager.metaCommands);
        const promise = firstValueFrom(this.apiClient
            .post<ICommand[], IEventTransferObject>(TRANSACTION_ENDPOINT, commandsToPush, false, null, metadataHeader));

        promise.then((response) => {
            response.events.forEach(this.eventService.emit);
        });
        this.commandsPushed.next(this.transactionCommandQueue);
        return promise;
    }

    pushCommandQueueTransaction(): Observable<any> {
        const commandsToPush = this.transactionCommandQueue;
        const subject = this.pushCommandsInternal(commandsToPush);
        subject.subscribe((response: any) => {
            if (ObjectUtils.hasValue(response)) {
                response.events.forEach(this.eventService.emit);
            }
        });
        this.transactionCommandQueue = [];
        return subject.asObservable();
    }

    pushCommandStack() {
        const promise = firstValueFrom(this.apiClient
            .post<ICommand[], IEventTransferObject>(TRANSACTION_ENDPOINT, this.transactionCommandQueue, false));
        this.transactionCommandQueue = [];

        promise.then((response) => {
            response.events.forEach(this.eventService.emit);
            this.commandsPushed.next(this.transactionCommandQueue);
        });
        return promise;
    }

    async reset() {
        this.transactionCommandQueue = [];
    }

    isDirty(): boolean {
        return this.transactionCommandQueue && this.transactionCommandQueue.length > 0;
    }

    createInstance() {
        return new CommandService(this.apiClient, this.eventService);
    }

    private pushCommandsInternal(commands: ICommand[]) {
        const subject = new Subject<IEventTransferObject>();
        this.apiClient.post<ICommand[], IEventTransferObject>(TRANSACTION_ENDPOINT, commands, false).subscribe(
            {
                next: (result) => {
                    this.commandsPushed.next(commands);
                    subject.next(result);
                    subject.complete();
                },
                error: (e) => {
                    subject.error(e);
                }
            }
        );

        return subject;
    }

    private getMetaCommandsAsHeader(metaCommands: IMetaCommand[]): HttpHeaders {
        let hdrs = new HttpHeaders();
        if (metaCommands.length > 0) {
            hdrs = hdrs.set("CQSMetadata", btoa(JSON.stringify(metaCommands)));
        }
        return hdrs;
    }
}
